import { useState, useEffect, useMemo } from 'react';
import { useTheme } from './theme';

const isBrowser = typeof window !== 'undefined';

const parseBreakpointValue = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return NaN;

  const pxMatch = value.match(/^(\d+(?:\.\d+)?)px$/);
  if (pxMatch) return Number(pxMatch[1]);

  const emMatch = value.match(/^(\d+(?:\.\d+)?)(em|rem)$/);
  if (emMatch) return Number(emMatch[1]) * 16;

  const num = Number(value);
  return Number.isNaN(num) ? NaN : num;
};

const buildBreakpointEntries = (breakpoints = {}) => {
  const parsed = Object.entries(breakpoints || {})
    .map(([key, value]) => ({ key, minWidth: parseBreakpointValue(value) }))
    .filter((entry) => !Number.isNaN(entry.minWidth))
    .sort((a, b) => a.minWidth - b.minWidth);

  const seen = new Set();
  const ordered = [];

  parsed.forEach((entry) => {
    if (entry.key === 'base' || seen.has(entry.key)) return;
    seen.add(entry.key);
    ordered.push(entry);
  });

  return [{ key: 'base', minWidth: 0 }, ...ordered];
};

const createValueMap = (values, keys) => {
  const map = {};
  if (Array.isArray(values)) {
    keys.slice(0, values.length).forEach((key, index) => {
      const val = values[index];
      if (typeof val !== 'undefined') {
        map[key] = val;
      }
    });
  } else if (values && typeof values === 'object') {
    Object.entries(values).forEach(([key, value]) => {
      map[key] = value;
    });
  } else if (typeof values !== 'undefined') {
    map.base = values;
  }
  return map;
};

const getFirstDefinedValue = (valueMap, keys) => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(valueMap, key)) {
      return valueMap[key];
    }
  }
  return undefined;
};

const resolveValueForViewport = (width, entries, valueMap) => {
  if (typeof width !== 'number') return undefined;
  let matched;

  entries.forEach((entry) => {
    if (entry.key === 'base') {
      if (Object.prototype.hasOwnProperty.call(valueMap, 'base')) {
        matched = valueMap.base;
      }
      return;
    }

    if (width >= entry.minWidth && Object.prototype.hasOwnProperty.call(valueMap, entry.key)) {
      matched = valueMap[entry.key];
    }
  });

  return matched;
};

export const useBreakpointValue = (values, options = {}) => {
  const theme = useTheme();
  const { breakpoints: customBreakpoints, fallback } = options;
  const breakpointSource = customBreakpoints ?? theme?.breakpoints ?? {};

  const entries = useMemo(() => buildBreakpointEntries(breakpointSource), [breakpointSource]);
  const breakpointKeys = useMemo(() => entries.map((entry) => entry.key), [entries]);

  const [viewportWidth, setViewportWidth] = useState(() =>
    isBrowser ? window.innerWidth : undefined
  );

  useEffect(() => {
    if (!isBrowser) return undefined;

    const updateWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const valueMap = useMemo(() => createValueMap(values, breakpointKeys), [values, breakpointKeys]);

  const resolvedValue = useMemo(() => {
    const fallbackValue = typeof fallback !== 'undefined'
      ? fallback
      : getFirstDefinedValue(valueMap, breakpointKeys);

    if (typeof viewportWidth === 'undefined') {
      return fallbackValue;
    }

    const match = resolveValueForViewport(viewportWidth, entries, valueMap);
    return typeof match === 'undefined' ? fallbackValue : match;
  }, [viewportWidth, entries, valueMap, fallback, breakpointKeys]);

  return resolvedValue;
};
